import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { isEmpty } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { getLTSessionTypeAndRate } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import {
  PAYMENT_TYPES,
  programConfig as PROGRAM_CONFIG,
  ProgramConfig,
  ROLES,
} from '../../../../common/constants/appConstants'
import { hasRole, isECDCProvider, resolveDAEngageProgramName } from '../../../../common/utils/utils'
import { getAuthUser } from '../../../../data/auth/authSelectors'
import { getHealthPlanICD10s } from '../../../../data/healthPlan/healthPlanSelectors'
import { getHPEligibility, getICD10s } from '../../../../data/healthPlanAutoActions'
import { getClientCurrentSessionCount } from '../../../../data/lyraTherapy/clientSelectors'
import {
  getRequestPaymentDiffPricingEnabled,
  getRequestPaymentDiffPricingModifier,
  getRequestPaymentRates,
} from '../../../../data/requestPayment/requestPaymentSelectors'
import { AppDispatch, useAppDispatch } from '../../../../data/store'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { getContentsByProgramId } from '../../../data/ltContentsAutoActions'

const dispatchAutoAction = <T = Record<string, unknown>>(dispatch: AppDispatch, action: unknown): Promise<T> =>
  (dispatch as unknown as (autoAction: unknown) => Promise<T>)(action)

export type CloseSessionEligibilityResult = {
  sessionDetails: { sessionType: string; sessionRate: string } | null
  isUsingSelfPay: boolean
  useEnhancedDiagnosisCapture: boolean
  didHPEligibilityAPIFail: boolean
  isLoading: boolean
  hasFetchedHPEligibility: boolean
  eligibilityObj: Record<string, unknown>
  programConfig: ProgramConfig | undefined
  shouldShowGuardianConsent: boolean
}

export const useCloseSessionEligibility = ({
  appointment,
  episode,
  modality,
  isNonAttendedSession,
  isLateClientCancellation,
}: {
  appointment: Appointment | undefined
  episode: Episode | undefined
  modality: string | undefined
  isNonAttendedSession: boolean
  isLateClientCancellation: boolean
}): CloseSessionEligibilityResult => {
  const user = useSelector(getAuthUser)
  const { data: clientDetails } = useSelectedClientData()
  const sessionsRates = useSelector(getRequestPaymentRates)
  const differentialPricingEnabled = useSelector(getRequestPaymentDiffPricingEnabled)
  const differentialPricingModifier = useSelector(getRequestPaymentDiffPricingModifier)
  const icd10Codes = useSelector(getHealthPlanICD10s)
  const dispatch = useAppDispatch()
  const currentSessionCount = useSelector(getClientCurrentSessionCount) || 0
  const isProviderEngageDA = useIsEngageDAProvider()

  const { shouldUseInternationalCoachingSesssionType, isSelfPayEnabled, showEnhancedLCxDiagnosis, isECDCEnabled } =
    useFlags()

  const programConfig: ProgramConfig | undefined = episode ? PROGRAM_CONFIG[episode.program_name] : undefined

  const enhancedLCxDiagnosisEnabled = programConfig?.enhancedLCxDiagnosisEnabled && showEnhancedLCxDiagnosis

  const shouldDAProviderUseECDC =
    isECDCEnabled && isProviderEngageDA && (isECDCProvider(user!) || hasRole(user!.roles, ROLES.PAYMENTS_ADMIN))

  const useEnhancedDiagnosisCapture = enhancedLCxDiagnosisEnabled || shouldDAProviderUseECDC

  const shouldShowGuardianConsent = episode?.program_name === ProgramNames.TeensTherapy && currentSessionCount === 1

  const sessionDetails = useMemo(() => {
    const resolvedModality =
      isProviderEngageDA && !isNonAttendedSession && !isLateClientCancellation
        ? (() => {
            try {
              return JSON.parse(modality ?? '{}')?.type ?? ''
            } catch {
              return ''
            }
          })()
        : (appointment?.meetingFormat ?? '')

    return episode && episode.program_name
      ? getLTSessionTypeAndRate({
          programName: resolveDAEngageProgramName(episode.program_name, isProviderEngageDA),
          sessionNumber: appointment?.sessionNumber ?? 1,
          sessionsRates: sessionsRates ?? {},
          dateOfBirth: clientDetails?.date_of_birth,
          modality: resolvedModality,
          differentialPricingEnabled: differentialPricingEnabled ?? false,
          differentialPricingModifier: differentialPricingModifier ?? 0,
          isInternationalCoachingSessionTypeEnabled: shouldUseInternationalCoachingSesssionType,
          clientCountry: clientDetails?.country,
          bookedSessionType: appointment?.sessionType,
          // Prefer the backend-provided override on retroactive appointments (mirrors EndOfSession /
          // SessionCancellation) so the flexible-note charge and HP eligibility use the same type. See NETEN-2349.
          suggestedSessionType: appointment?.suggestedSessionType,
        })
      : null
  }, [
    isProviderEngageDA,
    isNonAttendedSession,
    isLateClientCancellation,
    modality,
    appointment?.meetingFormat,
    appointment?.sessionNumber,
    appointment?.sessionType,
    appointment?.suggestedSessionType,
    episode,
    sessionsRates,
    clientDetails?.date_of_birth,
    clientDetails?.country,
    differentialPricingEnabled,
    differentialPricingModifier,
    shouldUseInternationalCoachingSesssionType,
  ])

  const { sessionType } = sessionDetails ?? {}

  const {
    employer,
    gender,
    last_name: lastName,
    first_name: firstName,
    date_of_birth: dateOfBirth,
    id: clientId,
    lyra_code: lyraCode,
  } = clientDetails ?? {}

  const healthPlanEligibilityPayload = useMemo(
    () => ({
      ...(lyraCode && { lyra_code: lyraCode }),
      visit_date: appointment?.startDate,
      provider_lyra_id: user?.id,
      session_type: sessionType ?? null,
      company: employer,
      gender,
      date_of_birth: dateOfBirth,
      last_name: lastName,
      first_name: firstName,
      ...(clientId && { patient_lyra_id: clientId }),
      appointment_id: appointment?.appointmentId,
    }),
    [
      lyraCode,
      appointment?.startDate,
      user?.id,
      sessionType,
      employer,
      gender,
      dateOfBirth,
      lastName,
      firstName,
      clientId,
      appointment?.appointmentId,
    ],
  )

  const shouldFetch = !!appointment && !!episode
  const shouldFetchHPEligibility = shouldFetch && !!sessionType

  const [eligibilityObj, setEligibilityObj] = useState<Record<string, unknown>>({})
  const [isLoadingHPEligibility, setIsLoadingHPEligibility] = useState(false)
  const [hasFetchedHPEligibility, setHasFetchedHPEligibility] = useState(false)

  useEffect(() => {
    if (!shouldFetchHPEligibility) {
      return
    }
    let active = true
    setIsLoadingHPEligibility(true)
    dispatchAutoAction(dispatch, getHPEligibility(healthPlanEligibilityPayload))
      .then((response) => {
        if (active) {
          setEligibilityObj(response ?? {})
        }
      })
      .catch(() => {
        if (active) {
          setEligibilityObj({})
        }
      })
      .finally(() => {
        if (active) {
          setIsLoadingHPEligibility(false)
          setHasFetchedHPEligibility(true)
        }
      })
    return () => {
      active = false
    }
  }, [shouldFetchHPEligibility, healthPlanEligibilityPayload, dispatch])

  useEffect(() => {
    if (!shouldFetch) {
      return
    }

    dispatchAutoAction(dispatch, getICD10s({})).catch(() => {})
    if (isProviderEngageDA && episode?.program_id) {
      dispatchAutoAction(dispatch, getContentsByProgramId({ programId: episode.program_id })).catch(() => {})
    }
  }, [shouldFetch, isProviderEngageDA, episode?.program_id, dispatch])

  const { next_session_uses_health_plan: nextSessionUsesHealthPlan, payment_type: paymentType } = eligibilityObj ?? {}

  const isUsingSelfPay =
    isSelfPayEnabled &&
    paymentType === PAYMENT_TYPES.SELF_PAY &&
    !!Programs.getProgramConfigByName(episode?.program_name)?.selfPayEligible

  const didHPEligibilityAPIFail = hasFetchedHPEligibility && nextSessionUsesHealthPlan === undefined

  const isLoading = isLoadingHPEligibility || !hasFetchedHPEligibility || isEmpty(icd10Codes)

  return {
    sessionDetails,
    isUsingSelfPay,
    useEnhancedDiagnosisCapture,
    didHPEligibilityAPIFail,
    isLoading,
    hasFetchedHPEligibility,
    eligibilityObj: eligibilityObj ?? {},
    programConfig,
    shouldShowGuardianConsent,
  }
}
